/**
 * Single source of truth for plans-v4 mock fund identity + execution validations role.
 *
 * - List mocks (lumpsum/sip) must pull isin/fundName/amc via `planMockFundIdentity(key)`.
 * - MSW validations on `/allocation` resolve roles from this catalog only.
 *
 * To add a demo path: add an entry here, use it in the list mock, done.
 */
import { getFundLogoPath, MockFundAmc } from '../../../../../data/mockUtils'

/** Outcomes exercised by usePlanBuyAction / usePlanSellAction / usePlanSipAction. */
export type PlanExecutionRole =
  | 'ready'
  | 'notOnboarded'
  | 'buyDisabled'
  | 'sipDisabled'
  | 'sellRegular'
  | 'error'

export type PlanMockFund = {
  isin: string
  fundName: string
  amc: MockFundAmc
  /** Which MF execution validations branch this fund should hit on allocation pages. */
  execution: PlanExecutionRole
}

/**
 * Keys are stable handles for list mocks — prefer these over raw ISIN strings.
 * Roles apply to the fund’s ISIN wherever it appears (allocation, rebalance-from-allocation, ideal, sip).
 */
export const PLAN_MOCK_FUNDS = {
  // --- Buy / Start SIP ---
  // PPFAS gift fund — usePpfasGiftFundHandling (buy + start SIP + MF L2 Invest)
  buyPpfasGiftFundEnquiry: {
    isin: 'INPPFASNDQ10',
    fundName: 'Parag Parikh IFSC Nasdaq 100 Fund of Fund Direct Growth',
    amc: MockFundAmc.PPFAS,
    execution: 'ready'
  },
  buyReady: {
    isin: 'INF846K01EW2',
    fundName: 'Axis Bluechip Fund',
    amc: MockFundAmc.AXIS,
    execution: 'ready'
  },
  buyDisabled: {
    isin: 'INF209K01BB1',
    fundName: 'SBI Bluechip Fund',
    amc: MockFundAmc.SBI,
    execution: 'buyDisabled'
  },
  buyNotOnboarded: {
    isin: 'INF194K01DS1',
    fundName: 'DSP Top 100 Equity Fund',
    amc: MockFundAmc.DSP,
    execution: 'notOnboarded'
  },

  // --- Sell (allocation Large Cap sell bucket) ---
  sellReady: {
    isin: 'INF179K01LC2',
    fundName: 'HDFC Large Cap Fund',
    amc: MockFundAmc.HDFC,
    execution: 'ready'
  },
  sellRegular: {
    isin: 'INF109K01YH5',
    fundName: 'ICICI Prudential Bluechip Fund',
    amc: MockFundAmc.ICICI_PRUDENTIAL,
    execution: 'sellRegular'
  },

  // --- SIP start (sip overview Start SIP demo ISIN) ---
  sipStartDisabled: {
    isin: 'INF846K01STR8',
    fundName: 'HDFC Bluechip Fund – Growth',
    amc: MockFundAmc.HDFC,
    execution: 'sipDisabled'
  },

  // --- Optional network-error toast (add this isin to a list card to demo) ---
  buyError: {
    isin: 'INF000ERROR1',
    fundName: 'Execution Error Demo Fund',
    amc: MockFundAmc.DEFAULT,
    execution: 'error'
  }
} as const satisfies Record<string, PlanMockFund>

export type PlanMockFundKey = keyof typeof PLAN_MOCK_FUNDS

/** isin + fundName + amc for list/recommendation builders. */
export function planMockFundIdentity(key: PlanMockFundKey): {
  isin: string
  fundName: string
  amc: MockFundAmc
} {
  const fund = PLAN_MOCK_FUNDS[key]
  return {
    isin: fund.isin,
    fundName: fund.fundName,
    amc: fund.amc
  }
}

/** isin + fundName + logo path — ideal / list rows that take mfLogoUrl directly. */
export function planMockFundWithLogo(key: PlanMockFundKey): {
  isin: string
  fundName: string
  mfLogoUrl: string
} {
  const fund = planMockFundIdentity(key)
  return {
    isin: fund.isin,
    fundName: fund.fundName,
    mfLogoUrl: getFundLogoPath(fund.amc)
  }
}

export function getPlanExecutionRoleForIsin(isin: string): PlanExecutionRole {
  if (!isin) {
    return 'ready'
  }
  for (const fund of Object.values(PLAN_MOCK_FUNDS)) {
    if (fund.isin === isin) {
      return fund.execution
    }
  }
  return 'ready'
}
